import { useCallback, useId } from 'react';
import { useResultsFileUpload } from '@/features/quality/sections/useResultsFileUpload';
import { invalidateGridQuery, queryKeys, useInvalidateQueries } from '@/shared/hooks';
import type { EntityTrendsPageConfig } from './useEntityTrendsPage.types';

type UseEntityTrendsUploadFlowParams<TEntityItem, TSummary extends { total: number }> = {
  config: EntityTrendsPageConfig<TEntityItem, TSummary>;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

type UseEntityTrendsUploadFlowResult = {
  uploadProjectIdFieldId: string;
  uploadFlow: ReturnType<typeof useResultsFileUpload>;
};

export const useEntityTrendsUploadFlow = <TEntityItem, TSummary extends { total: number }>({
  config,
  selectedOrganisationId,
  selectedProjectId,
}: UseEntityTrendsUploadFlowParams<TEntityItem, TSummary>): UseEntityTrendsUploadFlowResult => {
  const uploadProjectIdFieldId = useId();
  const invalidateQueries = useInvalidateQueries();
  const handleUploadRefresh = useCallback(() => {
    void invalidateQueries([
      invalidateGridQuery(config.gridPath),
      invalidateGridQuery(config.executionGridPath),
      { queryKey: config.summaryPrefixKey(), exact: false },
      { queryKey: config.executionSummaryPrefixKey(), exact: false },
      { queryKey: queryKeys.metricsPrefix(), exact: false },
    ]);
  }, [config, invalidateQueries]);
  const uploadFlow = useResultsFileUpload({
    selectedProjectId,
    selectedOrganisationId,
    onRefresh: handleUploadRefresh,
  });

  return { uploadProjectIdFieldId, uploadFlow };
};
